import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MediaFolder } from '@/entities/brand-media';

/**
 * Where to file what is selected, as one control both surfaces share.
 *
 * The bulk bar files several rows and the detail panel files the one it is describing, but the
 * question and its answers are the same either way, so the folder list is written once rather than
 * kept in step by hand.
 *
 * A menu rather than a select, because this is an action and not a field: the trigger keeps saying
 * what pressing it does however many times an asset is moved, and a select would have started
 * reading back the last answer instead. Where the asset already lives is said by the tick inside,
 * which is what a menu can show and a placeholder cannot.
 */
export const MoveToFolderMenu = ({
  folders,
  label,
  currentFolderId,
  className,
  onMove,
}: {
  folders: readonly MediaFolder[];
  /** What the trigger says. Named per surface: one asset and a selection of them read differently. */
  label: string;
  /**
   * The folder to tick. `null` is unfiled, which ticks "No folder"; leaving it out means nothing is
   * ticked, which is the honest answer for a selection spanning several folders and for a backend
   * that does not report the folder at all.
   */
  currentFolderId?: string | null;
  className?: string;
  /** Raised with the chosen destination, `null` to take the asset out of every folder. */
  onMove: (folderId: string | null) => void;
}) => {
  const { t } = useTranslation('tools');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          {label}
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      {/* Aligned to the trigger's leading edge rather than centred on it, so the menu opens along
          the edge the words start at instead of straddling it. */}
      <DropdownMenuContent align="start">
        <DropdownMenuCheckboxItem checked={currentFolderId === null} onSelect={() => onMove(null)}>
          {t('brandMedia.noFolder')}
        </DropdownMenuCheckboxItem>
        {folders.map((folder) => (
          <DropdownMenuCheckboxItem
            key={folder.id}
            checked={currentFolderId === folder.id}
            onSelect={() => onMove(folder.id)}
          >
            {folder.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
